import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { useObjectScope } from '@/shared/object-scope';
import { ObjectScopeList } from './ObjectScopeList';
import { useObjectListMissing } from './ObjectScopeStatus';

export function scopeInitials(name: string): string {
  const [first = '', second = ''] = name.trim().split(/\s+/).filter(Boolean);
  const letters = second ? `${first.charAt(0)}${second.charAt(0)}` : first.slice(0, 2);
  return letters.toUpperCase();
}

/**
 * Objektwahl der Seitenleiste. Die Auswahl gilt in allen Bereichen und bleibt gespeichert
 * (`useObjectScope`); eingeklappt zeigt die Leiste nur die Kachel mit den Anfangsbuchstaben.
 */
export function ObjectScopeMenu({ compact = false }: { compact?: boolean }) {
  const scope = useObjectScope();
  const [open, setOpen] = useState(false);
  const missing = useObjectListMissing();
  const name = missing ? 'Objektliste fehlt' : (scope.group?.name ?? 'Alle erlaubten Objekte');
  const meta = missing
    ? scope.savedGroupId
      ? 'Ihre Auswahl greift gerade nicht'
      : 'Zum erneuten Laden öffnen'
    : scope.group
      ? `${scope.group.visibleApartmentCount} Apartments`
      : 'jedes Objekt mit Zugriff';
  const tile = (
    <span
      className={cn(
        'flex size-9 flex-none items-center justify-center rounded-md text-xs font-extrabold',
        missing ? 'bg-amber-tint text-ink' : 'bg-forest-deep text-white',
      )}
    >
      {missing ? (
        <TriangleAlert size={16} aria-hidden />
      ) : scope.group ? (
        scopeInitials(scope.group.name)
      ) : (
        <Check size={15} aria-hidden />
      )}
    </span>
  );
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        {compact ? (
          <button
            type="button"
            aria-label={`Objekt: ${name}`}
            title={`Objekt: ${name}`}
            className="focus-visible:outline-forest rounded-square flex size-11 items-center justify-center"
          >
            {tile}
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Objekt: ${name}`}
            className={cn(
              'flex min-h-[54px] w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left',
              open ? 'border-forest border-2 bg-white' : 'border-hairline bg-paper border',
            )}
          >
            {tile}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-ink truncate text-sm font-bold">{name}</span>
              <span className="text-ink-soft truncate text-xs">{meta}</span>
            </span>
            <ChevronDown size={14} aria-hidden className="text-ink flex-none" />
          </button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="start"
          sideOffset={12}
          collisionPadding={12}
          className="border-hairline shadow-panel rounded-tile z-50 w-[340px] max-w-[calc(100vw-24px)] overflow-hidden border bg-white"
        >
          <ObjectScopeList onPicked={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
